import { useState, useRef } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Button } from '@mui/material';
import {
  // ColorUtil,
  GlobalActions,
  TimeUtil,
  VibeIcon,
  viDragIndicator,
  // viClose,
  viLoopImage,
  viLoopVideo,
  color,
} from 'vibeguide';
import './LoopDefinitionItem.scss';

let currentY = 0;

function LoopDefinitionItem({
  className = '',
  style = {},
  item = {},
  index,
  dragging,
  onRemove = () => {},
  onChangeOrder = () => {},
  setDrag,
  resetDrag,
}) {
  const [dropAt, setDropAt] = useState(null);
  const itemRef = useRef(null);

  const onDragStart = (e) => {
    const {
      dataTransfer,
    } = e;

    dataTransfer.setData('data', JSON.stringify(item));

    setDrag({
      dragging: true,
      dragType: 'Loop Template',
    });
  };

  const onDragEnd = () => {
    resetDrag();
    setDropAt(null);
  };

  /**
   * Dragging over the droppable element
   */
  const onDragOver = (e) => {
    e.preventDefault();

    const {
      clientY,
    } = e;

    if (currentY === clientY) {
      return;
    }

    const itemRect = itemRef.current.getBoundingClientRect();
    const {
      top,
      height,
    } = itemRect;

    // get the halfway mark for the cell
    const halfHeight = height / 2;
    const halfway = top + halfHeight;
    const isTopHalf = clientY <= halfway;

    if (isTopHalf && dropAt !== 'above') {
      setDropAt('above');
    } else if (!isTopHalf && dropAt !== 'below') {
      setDropAt('below');
    }

    currentY = clientY;
  };

  /**
   * When the cursor is no longer dragging over the element
   */
  const onDragLeave = () => {
    setDropAt(null);
  };

  /**
   * Drop an item
   */
  const onDrop = (e) => {
    const {
      dataTransfer,
    } = e;

    const dropData = JSON.parse(dataTransfer.getData('data'));

    if (item._id === dropData._id) {
      // do not allow dropping in the same position
      console.warn('Cannot drop an item in the same spot');
      return;
    }

    setDropAt(null);

    onChangeOrder({
      dropItem: dropData,
      onItem: item,
      at: dropAt,
    });
  };

  /**
   * User changes the number input box

  const onChangeNumber = ({
    target: {
      name,
      value,
    },
  }) => {
    onUpdate({
      ...item,
      [name]: toInteger(value),
    });
  };
     */

  /**
   * Remove an item
   */
  const onClickRemoveItem = () => {
    onRemove(item);
  };

  /**
   * Get the icon type
   */
  const getIconType = (iconType) => {
    switch (iconType) {
      case 'image':
        return viLoopImage;
      case 'video':
        return viLoopVideo;
      default:
        return viLoopImage;
    }
  };

  const getMinAndSeconds = () => {
    const loopItemMinutesSeconds = TimeUtil.convertSeconds(item.durationSeconds).split(':');
    let minSecDiv = null;
    if (loopItemMinutesSeconds[0] > 0) {
      minSecDiv = <span>{parseInt(loopItemMinutesSeconds[0], 10)} min</span>;
    }
    const finalDiv = minSecDiv ? <div>{minSecDiv} <span>{loopItemMinutesSeconds[1]} sec</span></div>
      : <div><span>{parseInt(loopItemMinutesSeconds[1], 10)} sec</span></div>;
    return finalDiv;
  };

  return (
    <div
      className={classNames('LoopDefinitionItem', className)}
      style={{
        ...style,
        position: 'relative',
      }}
    >
      <div
        ref={itemRef}
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          margin: '4px 0',
        }}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        draggable
      >
        <VibeIcon
          icon={viDragIndicator}
          color={color.manatee}
          hoverColor={color.obsidian}
          size={24}
        />

        <div className="loop-item-details">
          <div
            style={{
              flexGrow: 1,
              width: 100,
              marginRight: 16,
            }}
          >
            <div className="loop-item-name">
              <VibeIcon
                icon={getIconType(item.mediaType)}
                color={color.primary}
                tooltip="Image"
                tooltipProps={{
                  placement: 'top',
                }}
              />
              <span>{item.name}</span>
            </div>
          </div>

          <span
            className="loop-time-label"
          >
            {getMinAndSeconds()}
          </span>
        </div>

        <Button
          className="loop-item-remove"
          onClick={onClickRemoveItem}
          tabIndex={index + 6}
        >
          Remove
        </Button>

        {dragging && (
          <div
            className="drag-overlay"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              zIndex: 2,
            }}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
          />
        )}
      </div>

      {dropAt === 'above' && (
        <div
          style={{
            position: 'absolute',
            top: -3,
            left: -8,
            right: -8,
            height: 2,
            backgroundColor: color.primary,
          }}
        />
      )}

      {dropAt === 'below' && (
        <div
          style={{
            position: 'absolute',
            bottom: -3,
            left: -8,
            right: -8,
            height: 2,
            backgroundColor: color.primary,
          }}
        />
      )}
    </div>
  );
}

LoopDefinitionItem.propTypes = {
  className: PropTypes.string,
  style: PropTypes.object,
  item: PropTypes.object,
  onRemove: PropTypes.func,
  onChangeOrder: PropTypes.func,
};

function mapStateToProps(state) {
  return {
    dragging: state.global.drag.dragging,
  };
}

const mapDispatchToProps = {
  setDrag: GlobalActions.setDrag,
  resetDrag: GlobalActions.resetDrag,
};

export default connect(mapStateToProps, mapDispatchToProps)(LoopDefinitionItem);
